import type {
  DocumentHead,
  CharProperty,
  ParaProperty,
  FontFace,
  FontLang,
  Style,
  BorderFill,
  BorderLine,
  BorderLineType,
  BorderWidth,
  UnderlineType,
  StrikeoutType,
  Alignment,
  SlashType,
  FillBrush,
  BulletProperty,
  NumberingProperty,
  NumberingLevel,
  NumberFormat,
} from '../../../types.js';
import {
  parseXml,
  findChild,
  findChildren,
  stripNs,
  attrInt,
  attrStr,
  attrBool,
  type XmlNode,
} from './parser.js';

export function parseHeaderXml(xml: string): DocumentHead {
  const root = parseXml(xml);

  // 실제 한글 파일은 hh:refList 래퍼 안에 fontfaces/charProperties 등이 있음
  const refList = findChild(root, 'hh:refList') || root;

  return {
    fontFaces: parseFontFaces(refList),
    borderFills: parseBorderFills(refList),
    charProperties: parseCharProperties(refList),
    paraProperties: parseParaProperties(refList),
    styles: parseStyles(refList),
    bulletProperties: parseBullets(refList),
    numberingProperties: parseNumberings(refList),
    compatibleDoc: parseCompatibleDoc(root),
  };
}

function parseFontFaces(root: XmlNode): FontFace[] {
  const faces: FontFace[] = [];
  const fontfacesNode = findChild(root, 'hh:fontfaces');
  if (!fontfacesNode) return faces;

  for (const faceNode of findChildren(fontfacesNode, 'hh:fontface')) {
    const lang = attrStr(faceNode, 'lang', 'HANGUL') as FontLang;
    for (const fontNode of findChildren(faceNode, 'hh:font')) {
      faces.push({
        lang,
        fontName: attrStr(fontNode, 'face'),
        fontType: (attrStr(fontNode, 'type', 'ttf') as 'ttf' | 'hps' | 'unknown'),
      });
    }
  }

  return faces;
}

function parseBorderFills(root: XmlNode): BorderFill[] {
  const fills: BorderFill[] = [];
  const fillsNode = findChild(root, 'hh:borderFills');
  if (!fillsNode) return fills;

  for (const bfNode of findChildren(fillsNode, 'hh:borderFill')) {
    const leftBorder = findChild(bfNode, 'hc:leftBorder');
    const rightBorder = findChild(bfNode, 'hc:rightBorder');
    const topBorder = findChild(bfNode, 'hc:topBorder');
    const bottomBorder = findChild(bfNode, 'hc:bottomBorder');

    let fillBrush: FillBrush | undefined;
    const fillBrushNode = findChild(bfNode, 'hc:fillBrush');
    if (fillBrushNode) {
      const winBrush = findChild(fillBrushNode, 'hc:winBrush');
      if (winBrush) {
        fillBrush = {
          type: 'SOLID',
          faceColor: parseColor(attrStr(winBrush, 'faceColor', '#000000')),
          patternColor: winBrush.attrs['hatchColor']
            ? parseColor(attrStr(winBrush, 'hatchColor'))
            : undefined,
        };
      }
    }

    fills.push({
      id: attrInt(bfNode, 'id'),
      threeD: attrBool(bfNode, 'threeD'),
      shadow: attrBool(bfNode, 'shadow'),
      slash: attrStr(bfNode, 'slash', 'NONE') as SlashType,
      backSlash: attrStr(bfNode, 'backSlash', 'NONE') as SlashType,
      borders: {
        left: parseBorderLine(leftBorder),
        right: parseBorderLine(rightBorder),
        top: parseBorderLine(topBorder),
        bottom: parseBorderLine(bottomBorder),
      },
      fillBrush,
    });
  }

  return fills;
}

function parseBorderLine(node: XmlNode | undefined): BorderLine {
  if (!node) {
    return { type: 'NONE', width: '0.1mm', color: 0 };
  }
  return {
    type: attrStr(node, 'type', 'NONE') as BorderLineType,
    width: attrStr(node, 'width', '0.1mm') as BorderWidth,
    color: parseColor(attrStr(node, 'color', '#000000')),
  };
}

function parseCharProperties(root: XmlNode): CharProperty[] {
  const props: CharProperty[] = [];
  const propsNode = findChild(root, 'hh:charProperties');
  if (!propsNode) return props;

  for (const cpNode of findChildren(propsNode, 'hh:charPr')) {
    const fontRefNode = findChild(cpNode, 'hh:fontRef');

    // bold/italic: 속성(우리 생성) 또는 자식 요소(한글 생성) 둘 다 지원
    const hasBoldElement = !!findChild(cpNode, 'hh:bold');
    const hasItalicElement = !!findChild(cpNode, 'hh:italic');

    // underline/strikeout: 속성(우리) 또는 자식 요소(한글)
    const underlineNode = findChild(cpNode, 'hh:underline');
    const strikeoutNode = findChild(cpNode, 'hh:strikeout');

    const underline = underlineNode
      ? attrStr(underlineNode, 'type', 'NONE') as UnderlineType
      : attrStr(cpNode, 'underline', 'NONE') as UnderlineType;

    const strikeout = strikeoutNode
      ? (attrStr(strikeoutNode, 'shape', 'NONE') === 'NONE' ? 'NONE' : 'CONTINUOUS') as StrikeoutType
      : attrStr(cpNode, 'strikeout', 'NONE') as StrikeoutType;

    props.push({
      id: attrInt(cpNode, 'id'),
      height: attrInt(cpNode, 'height', 1000),
      textColor: parseColor(attrStr(cpNode, 'textColor', '#000000')),
      shadeColor: cpNode.attrs['shadeColor'] && cpNode.attrs['shadeColor'] !== 'none'
        ? parseColor(attrStr(cpNode, 'shadeColor'))
        : undefined,
      bold: hasBoldElement || attrBool(cpNode, 'bold'),
      italic: hasItalicElement || attrBool(cpNode, 'italic'),
      underline,
      strikeout,
      useFontSpace: attrBool(cpNode, 'useFontSpace'),
      useKerning: attrBool(cpNode, 'useKerning'),
      spacing: cpNode.attrs['spacing'] !== undefined ? attrInt(cpNode, 'spacing') : undefined,
      relSize: cpNode.attrs['relSz'] !== undefined ? attrInt(cpNode, 'relSz') : undefined,
      charOffset: cpNode.attrs['offset'] !== undefined ? attrInt(cpNode, 'offset') : undefined,
      fontRef: fontRefNode
        ? {
            hangul: attrInt(fontRefNode, 'hangul'),
            latin: attrInt(fontRefNode, 'latin'),
            hanja: attrInt(fontRefNode, 'hanja'),
            japanese: attrInt(fontRefNode, 'japanese'),
            other: attrInt(fontRefNode, 'other'),
            symbol: attrInt(fontRefNode, 'symbol'),
            user: attrInt(fontRefNode, 'user'),
          }
        : { hangul: 0, latin: 0, hanja: 0, japanese: 0, other: 0, symbol: 0, user: 0 },
    });
  }

  return props;
}

function parseParaProperties(root: XmlNode): ParaProperty[] {
  const props: ParaProperty[] = [];
  const propsNode = findChild(root, 'hh:paraProperties');
  if (!propsNode) return props;

  for (const ppNode of findChildren(propsNode, 'hh:paraPr')) {
    // 한글 파일: hh:align 자식 요소에 horizontal 속성 / 우리: align 속성
    const alignNode = findChild(ppNode, 'hh:align');
    const alignment = alignNode
      ? attrStr(alignNode, 'horizontal', 'JUSTIFY') as Alignment
      : attrStr(ppNode, 'align', 'JUSTIFY') as Alignment;

    // margin/lineSpacing: 직접 또는 hp:switch > hp:case 안에 있을 수 있음
    let marginNode = findChild(ppNode, 'hh:parMargin') || findChild(ppNode, 'hh:margin');
    let lineSpacingNode = findChild(ppNode, 'hh:lineSpacing');

    // hp:switch > hp:case 안에서도 찾기
    const switchNode = findChild(ppNode, 'hp:switch');
    if (switchNode) {
      const caseNode = findChild(switchNode, 'hp:case');
      if (caseNode) {
        if (!marginNode) marginNode = findChild(caseNode, 'hh:margin');
        if (!lineSpacingNode) lineSpacingNode = findChild(caseNode, 'hh:lineSpacing');
      }
    }

    // hh:margin의 자식이 hc:left/hc:right 등인 경우 처리
    let paraMargin = { left: 0, right: 0, indent: 0, prevSpacing: 0, nextSpacing: 0 };
    if (marginNode) {
      const leftChild = findChild(marginNode, 'hc:left');
      if (leftChild) {
        // 한글 형식: <hh:margin><hc:left value="0" unit="HWPUNIT"/>...
        const intentChild = findChild(marginNode, 'hc:intent');
        const rightChild = findChild(marginNode, 'hc:right');
        const prevChild = findChild(marginNode, 'hc:prev');
        const nextChild = findChild(marginNode, 'hc:next');
        paraMargin = {
          left: leftChild ? attrInt(leftChild, 'value') : 0,
          right: rightChild ? attrInt(rightChild, 'value') : 0,
          indent: intentChild ? attrInt(intentChild, 'value') : 0,
          prevSpacing: prevChild ? attrInt(prevChild, 'value') : 0,
          nextSpacing: nextChild ? attrInt(nextChild, 'value') : 0,
        };
      } else {
        // 우리 형식: <hh:parMargin left="0" right="0".../>
        paraMargin = {
          left: attrInt(marginNode, 'left'),
          right: attrInt(marginNode, 'right'),
          indent: attrInt(marginNode, 'indent'),
          prevSpacing: attrInt(marginNode, 'prev'),
          nextSpacing: attrInt(marginNode, 'next'),
        };
      }
    }

    const headingNode = findChild(ppNode, 'hh:heading');

    props.push({
      id: attrInt(ppNode, 'id'),
      alignment,
      heading: headingNode
        ? attrStr(headingNode, 'type', 'NONE') as any
        : ppNode.attrs['heading'] ? ppNode.attrs['heading'] as any : undefined,
      breakBefore: ppNode.attrs['breakBefore'] ? (ppNode.attrs['breakBefore'] as any) : undefined,
      lineSpacing: lineSpacingNode
        ? {
            type: attrStr(lineSpacingNode, 'type', 'PERCENT') as any,
            value: attrInt(lineSpacingNode, 'value', 160),
          }
        : { type: 'PERCENT', value: 160 },
      paraMargin,
    });
  }

  return props;
}

function parseStyles(root: XmlNode): Style[] {
  const styles: Style[] = [];
  const stylesNode = findChild(root, 'hh:styles');
  if (!stylesNode) return styles;

  for (const sNode of findChildren(stylesNode, 'hh:style')) {
    styles.push({
      id: attrInt(sNode, 'id'),
      type: attrStr(sNode, 'type', 'PARA') as 'PARA' | 'CHAR',
      name: attrStr(sNode, 'name'),
      engName: sNode.attrs['engName'] || undefined,
      paraPrIDRef: sNode.attrs['paraPrIDRef'] !== undefined
        ? attrInt(sNode, 'paraPrIDRef')
        : undefined,
      charPrIDRef: sNode.attrs['charPrIDRef'] !== undefined
        ? attrInt(sNode, 'charPrIDRef')
        : undefined,
      nextStyleIDRef: sNode.attrs['nextStyleIDRef'] !== undefined
        ? attrInt(sNode, 'nextStyleIDRef')
        : undefined,
    });
  }

  return styles;
}

function parseNumberings(root: XmlNode): NumberingProperty[] {
  const result: NumberingProperty[] = [];
  const node = findChild(root, 'hh:numberings');
  if (!node) return result;

  for (const nNode of findChildren(node, 'hh:numbering')) {
    const levels: NumberingLevel[] = [];
    for (const lNode of findChildren(nNode, 'hh:paraHead')) {
      levels.push({
        format: attrStr(lNode, 'numFormat', 'DIGIT') as NumberFormat,
        start: attrInt(lNode, 'start', 1),
        prefix: lNode.attrs['prefix'] || undefined,
        suffix: lNode.attrs['suffix'] || undefined,
      });
    }
    result.push({ id: attrInt(nNode, 'id'), levels });
  }

  return result;
}

function parseBullets(root: XmlNode): BulletProperty[] {
  const result: BulletProperty[] = [];
  const node = findChild(root, 'hh:bullets');
  if (!node) return result;

  for (const bNode of findChildren(node, 'hh:bullet')) {
    result.push({
      id: attrInt(bNode, 'id'),
      bulletChar: attrStr(bNode, 'char', '●'),
      bulletSize: bNode.attrs['bulletSz'] !== undefined ? attrInt(bNode, 'bulletSz') : undefined,
    });
  }

  return result;
}

function parseCompatibleDoc(root: XmlNode): string | undefined {
  const node = findChild(root, 'hh:compatibleDocument');
  if (!node) return undefined;
  return attrStr(node, 'targetProgram') || undefined;
}

function parseColor(hex: string): number {
  const h = hex.replace('#', '');
  return parseInt(h, 16);
}
